/**
 * Authz Service (ORCH-333, ORCH-337, ORCH-21 §10.5, §11)
 *
 * Purpose: The Role Manager's calls to /core-api/authz: the permission and
 *          module catalog, the security dimensions, role reads and writes, and
 *          the access explainer behind the "Effective access" preview.
 * Dependencies: apiService (auth headers and the active tenant)
 */

import { apiService } from './api';
import {
  Dimension,
  DuplicateResult,
  ExplainableUser,
  Explanation,
  PermissionCatalog,
  Role,
  RoleErrorDetail,
  RoleWrite,
} from '../types/authz';

interface Envelope<T> {
  status: string;
  data: T;
}

class AuthzService {
  async getCatalog(): Promise<PermissionCatalog> {
    const response = await apiService.api.get<Envelope<PermissionCatalog>>('/authz/permissions');
    return response.data.data;
  }

  async getDimensions(): Promise<Dimension[]> {
    const response = await apiService.api.get<Envelope<Dimension[]>>('/authz/dimensions');
    return response.data.data;
  }

  /**
   * GLOBAL roles and the active tenant's own, inactive ones included. Each says
   * whether the caller may assign it; pass `userId` to judge that against the
   * person it would be assigned to (roles that inherit their attributes).
   */
  async listRoles(userId?: string, tenantId?: string): Promise<Role[]> {
    const response = await apiService.api.get<Envelope<Role[]>>('/authz/roles', {
      params: {
        ...(userId ? { user_id: userId } : {}),
        // Another tenant's roles, for a system admin in GLOBAL managing its members
        ...(tenantId ? { tenant_id: tenantId } : {}),
      },
    });
    return response.data.data;
  }

  async getRole(roleId: string): Promise<Role> {
    const response = await apiService.api.get<Envelope<Role>>(`/authz/roles/${roleId}`);
    return response.data.data;
  }

  async createRole(body: RoleWrite): Promise<Role> {
    const response = await apiService.api.post<Envelope<Role>>('/authz/roles', body);
    return response.data.data;
  }

  async updateRole(roleId: string, body: RoleWrite): Promise<Role> {
    const response = await apiService.api.put<Envelope<Role>>(`/authz/roles/${roleId}`, body);
    return response.data.data;
  }

  /** Copy a visible role into the active tenant; `adjusted` says what was changed to fit. */
  async duplicateRole(
    roleId: string,
    name: string,
    description?: string | null
  ): Promise<DuplicateResult> {
    const response = await apiService.api.post<Envelope<DuplicateResult>>(
      `/authz/roles/${roleId}/duplicate`,
      { name, description: description ?? null }
    );
    return response.data.data;
  }

  async setActive(roleId: string, active: boolean): Promise<Role> {
    const action = active ? 'reactivate' : 'deactivate';
    const response = await apiService.api.post<Envelope<Role>>(`/authz/roles/${roleId}/${action}`);
    return response.data.data;
  }

  async deleteRole(roleId: string): Promise<void> {
    await apiService.api.delete(`/authz/roles/${roleId}`);
  }

  /**
   * A member's effective access in the active tenant: their roles and every
   * permission's grants. Without `userId`, the caller's own; another member's
   * needs user.view and audit.view.
   */
  async explain(userId?: string, tenantId?: string): Promise<Explanation> {
    const response = await apiService.api.get<Envelope<Explanation>>('/authz/explain', {
      params: {
        ...(userId ? { user_id: userId } : {}),
        // Another tenant, for a system admin in GLOBAL (the User Manager's rule)
        ...(tenantId ? { tenant_id: tenantId } : {}),
      },
    });
    return response.data.data;
  }

  /** Members matching `search`, for picking whom to explain (needs user.view). */
  async searchMembers(search: string): Promise<ExplainableUser[]> {
    const response = await apiService.api.get<Envelope<ExplainableUser[]>>('/users/autocomplete', {
      params: { search, limit: 10 },
    });
    return response.data.data;
  }
}

/** The server's message for a refused role request, or a fallback. */
export const roleErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: RoleErrorDetail | string } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return detail?.message || fallback;
};

export const authzService = new AuthzService();
